import { useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChatFormItem,
  ChatFormItemType,
  ChatItem,
  ChatItemAnalyticsTag,
  ChatItemForm,
  ChatItemFormType,
  ChatItemLocationResults,
  ChatItemMessage,
  ChatItemSourceType,
  ChatItemType,
} from 'app/services/chatBot';
import useAutoOpenDelay from 'app/hooks/useAutoOpenDelay';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useFetchVirtualAssistant from 'app/hooks/fetch/useFetchVirtualAssistant';
import useSendChatbotAnswer from 'app/hooks/mutate/useSendChatbotAnswer';
import useSendChatbotMessage from 'app/hooks/mutate/useSendChatbotMessage';
import useSendChatbotDismiss from 'app/hooks/mutate/useSendChatbotDismiss';
import queryClient from 'app/services/queryClient';
import { v4 } from 'uuid';
import useLinks from 'app/modules/tailor/hooks/useLinks';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';

import { ChatHandlerParams } from '../types/chat';
import useVirtualAssistantTracking from './useVirtualAssistantTracking';
import useHandleResponse from './useHandleResponse';

export default function useVirtualAssistant() {
  const { openLink } = useLinks();
  const {
    captureChatOptionPresented,
    captureLinkClicked,
    captureLocationResultsDisplayed,
    captureAnswerSubmissionErrors,
    captureAnswerSubmitted,
    captureFormDismissed,
    captureFormPresented,
    captureMessageSent,
    capturePageview,
    captureQuerySubmitted,
  } = useVirtualAssistantTracking();
  const autoOpenDelay = useAutoOpenDelay();
  const { action } = useParams();
  const { isOpen } = useTailorIsOpen();
  const [chatItems, setChatItems] = useState<ChatItem[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingAreaAvailable, setTypingAreaAvailable] = useState(false);
  const [showTypingArea, setShowTypingArea] = useState(true);
  const [userMessage, setUserMessage] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { data: helpCenter } = useFetchHelpCenter();
  const {
    data: virtualAssistant,
    isLoading: isLoadingVirtualAssistant,
    isStale: isVirtualAssistantStale,
  } = useFetchVirtualAssistant({
    action,
    enabled: isOpen,
  });
  const { mutate: sendAnswer, data: answerResponse } = useSendChatbotAnswer();
  const { mutate: sendMessage, data: messageResponse } = useSendChatbotMessage();
  const { mutate: dismissQuestion, data: dismissResponse } = useSendChatbotDismiss();
  const visibleChatItems = useMemo(() => chatItems.filter((item) => item.visible), [chatItems]);

  const [userResponsePresent, setUserResponsePresent] = useState(false);
  const isConvAiActive = typingAreaAvailable;

  const handleError = () => {
    setIsTyping(false);
    setChatItems((prev) => [
      ...prev,
      {
        type: ChatItemType.MESSAGE,
        message: 'Something went wrong. Please try again.',
        visible: true,
        uuid: v4(),
        open_in_new_tab: false,
        url: '',
        source: ChatItemSourceType.BOT,
        identity: 'Bot',
        delay_duration: 0,
        aiResponse: false,
        errorMessage: true,
      },
    ]);
  };

  const handleAnswer = async ({
    answer,
    formItem,
    chatItem,
    options = { createMessage: true, showTypingArea: false },
    onError,
  }: ChatHandlerParams) => {
    if (!helpCenter?.uuid) return;
    setIsTyping(true);

    captureAnswerSubmitted(formItem, answer, isConvAiActive, chatItem.aiResponse);
    return sendAnswer(
      {
        uuid: helpCenter?.uuid,
        data: answer,
      },
      {
        onError: () => {
          handleError();
          onError?.();
        },
        onSuccess: (response) => {
          let hasErrors = false;
          response?.form?.items?.forEach((item: ChatFormItem) => {
            if (item.errors && item.errors.length > 0) {
              hasErrors = true;
              captureAnswerSubmissionErrors(item);
            }
          });
          if (hasErrors) {
            return;
          }

          if (options?.createMessage) {
            setChatItems((prev) => [
              ...prev,
              {
                type: ChatItemType.MESSAGE,
                message: answer.value,
                visible: true,
                uuid: v4(),
                open_in_new_tab: false,
                url: '',
                source: ChatItemSourceType.PATIENT,
                identity: 'User',
                delay_duration: 0,
                aiResponse: false,
              },
            ]);
          }
          if (options?.showTypingArea) {
            setShowTypingArea(true);
          }
        },
      }
    );
  };

  const handleDismiss = async ({
    answer,
    formItem,
    chatItem,
    options = { createMessage: true, showTypingArea: false },
  }: ChatHandlerParams) => {
    if (!helpCenter?.uuid) return;
    setIsTyping(true);
    if (options?.createMessage) {
      setChatItems((prev) => [
        ...prev,
        {
          type: ChatItemType.MESSAGE,
          message: answer.value,
          visible: true,
          uuid: v4(),
          open_in_new_tab: false,
          url: '',
          source: ChatItemSourceType.PATIENT,
          identity: 'User',
          delay_duration: 0,
          aiResponse: false,
        },
      ]);
    }
    if (options?.showTypingArea) {
      setShowTypingArea(true);
    }
    captureFormDismissed(formItem, isConvAiActive, chatItem.aiResponse);
    return dismissQuestion({
      uuid: helpCenter?.uuid,
      refId: answer.form_ref_id,
    });
  };

  const handleMessage = async (answer: string) => {
    if (!helpCenter?.uuid) return;
    setIsTyping(true);
    setChatItems((prev) => [
      ...prev,
      {
        type: ChatItemType.MESSAGE,
        message: answer,
        visible: true,
        uuid: v4(),
        open_in_new_tab: false,
        url: '',
        source: ChatItemSourceType.PATIENT,
        identity: 'User',
        delay_duration: 0,
        aiResponse: false,
        userMessage: true,
      },
    ]);
    sendMessage(
      { uuid: helpCenter?.uuid, data: { content: answer } },
      {
        onError: handleError,
      }
    );
    setUserMessage('');
    captureQuerySubmitted({ query: answer });
  };

  const handleContentClick = (e: React.MouseEvent, chatItem: ChatItemMessage) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const href = target?.closest('a')?.href;

    if (!href) return;
    captureLinkClicked(e, isConvAiActive, chatItem.aiResponse);
    openLink(href);
  };

  const handleDisplayFormItem = (formItem: ChatFormItem, chatItemForm: ChatItemForm) => {
    if (formItem.item_type === ChatFormItemType.SINGE_CHOICE) {
      captureChatOptionPresented(formItem.choices, isConvAiActive, chatItemForm.aiResponse);
    }
  };

  const addChatItems = (newChatItems: ChatItem[]) => {
    setIsTyping(true);
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const items = newChatItems
      .slice()
      .reverse()
      .filter((item) => !chatItems.find((i) => i.uuid === item.uuid));

    setChatItems((prev) => [...prev, ...items]);

    let totalDelay = 0;
    const itemsTagInfo: { [key: string]: { tagId?: string; tagName?: string; posInResponse?: number } } = {};
    items.forEach((item, i) => {
      if (item.type === ChatItemType.ENABLE_TYPING_AREA) {
        setTypingAreaAvailable(true);
      } else if (item.type === ChatItemType.DISABLE_TYPING_AREA) {
        setTypingAreaAvailable(false);
      } else if (item.type === ChatItemType.ANALYTICS_TAG) {
        const tagInfo = item as ChatItemAnalyticsTag;
        const nextMsg = items.find((_item, _idx) => _idx > i && _item.type === ChatItemType.MESSAGE);
        if (nextMsg !== undefined) {
          itemsTagInfo[nextMsg.uuid] = {
            tagId: tagInfo.tag_id,
            tagName: tagInfo.tag_name,
            posInResponse: i,
          };
        }
      }

      if (item.type === ChatItemType.FORM && (item as ChatItemForm).form_type === ChatItemFormType.CONTACT_FORM) {
        setShowTypingArea(false);
      }

      const delay = item.delay_duration || 0;
      timeouts.push(
        setTimeout(
          () => {
            setChatItems((prev) => prev.map((i) => (i.uuid === item.uuid ? { ...i, visible: true } : i)));
            if (i === items.length - 1) {
              setIsTyping(false);
            }
            if (Object.hasOwn(itemsTagInfo, item.uuid)) {
              captureMessageSent(item, isConvAiActive, item.aiResponse, itemsTagInfo[item.uuid]);
            } else if (item.type === ChatItemType.MESSAGE) {
              captureMessageSent(item, isConvAiActive, item.aiResponse);
            } else if (item.type === ChatItemType.FORM) {
              captureFormPresented(item as ChatItemForm, isConvAiActive, item.aiResponse);
            } else if (item.type === ChatItemType.LOCATION_RESULTS) {
              captureLocationResultsDisplayed(
                (item as ChatItemLocationResults).origin_zip,
                (item as ChatItemLocationResults).items.map(({ place_id }) => place_id)
              );
            }
          },
          (totalDelay += delay)
        )
      );
    });

    return timeouts;
  };

  useHandleResponse({
    response: virtualAssistant,
    addChatItems,
    setIsTyping,
    isStale: isVirtualAssistantStale,
  });

  useHandleResponse({
    response: answerResponse,
    addChatItems,
    setIsTyping,
    isStale: false,
  });

  useHandleResponse({
    response: dismissResponse,
    addChatItems,
    setIsTyping,
    isStale: false,
  });

  useHandleResponse({
    response: messageResponse,
    addChatItems,
    setIsTyping,
    isStale: false,
  });

  useEffect(() => {
    capturePageview(autoOpenDelay);
    return () => {
      setChatItems([]);
      queryClient.invalidateQueries({
        queryKey: ['virtualAssistant', helpCenter?.uuid, action],
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (answerResponse || messageResponse) {
      setUserResponsePresent(true);
    }
  }, [answerResponse, messageResponse]);

  return {
    chatItems,
    isTyping,
    typingAreaAvailable,
    userMessage,
    isLoadingVirtualAssistant,
    isVirtualAssistantStale,
    userResponsePresent,
    visibleChatItems,
    chatContainerRef,
    answerResponse,
    messageResponse,
    dismissResponse,
    setUserMessage,
    handleAnswer,
    handleDismiss,
    handleMessage,
    handleContentClick,
    handleDisplayFormItem,
    showTypingArea,
  };
}
